import * as React from 'react';

import type { ProductLookupRow } from '@graphql/queries/PerceptionModelQueries';
import { Box, Button, Chip, Typography } from '@mui/material';

import ProductDetailPanel from './ProductDetailPanel';

/**
 * What was clicked on the map: its profile, and whether it can join the norm
 * set.
 *
 * Anchors are addable like anything else. A norm set is a heuristic for
 * choosing products, not a partition, so a product may anchor a set it also
 * belongs to — it simply sits at distance 0 from itself.
 */

type Props = {
  product: string;
  row?: ProductLookupRow;
  rowLoading: boolean;
  anchorMean: Record<string, number>;
  attributes: string[];
  isAnchor: boolean;
  isIncluded: boolean;
  placeable: boolean;
  /** what the product is being added to; the explorer is not only for norm sets */
  targetLabel?: string;
  onToggle?: (product: string) => void;
};

const SelectedProductPanel: React.FC<Props> = ({
  product,
  row,
  rowLoading,
  anchorMean,
  attributes,
  isAnchor,
  isIncluded,
  placeable,
  targetLabel = 'norm set',
  onToggle,
}) => (
  <Box sx={{ mt: 2, borderTop: '1px solid rgba(0,0,0,0.08)', pt: 1 }}>
    <Typography variant="subtitle1">
      {product}
      {isAnchor && <Chip size="small" label="anchor" sx={{ ml: 1 }} />}
      {isIncluded && (
        <Chip size="small" color="primary" label="in norm set" sx={{ ml: 1 }} />
      )}
    </Typography>

    {row ? (
      <ProductDetailPanel
        row={row}
        anchorMean={anchorMean}
        attributes={attributes}
      />
    ) : (
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        {rowLoading
          ? 'Loading profile…'
          : 'This product has no row in this perception model.'}
      </Typography>
    )}

    {onToggle && (
      <Box sx={{ mt: 1 }}>
        <Button
          variant={isIncluded ? 'outlined' : 'contained'}
          disabled={!placeable}
          onClick={() => onToggle(product)}
        >
          {isIncluded ? `Remove from ${targetLabel}` : `Add to ${targetLabel}`}
        </Button>
        {/* An anchor may belong to the set it anchors — a norm set is a
            heuristic for choosing products, not a partition. It sits at
            distance 0 from itself, which is worth saying since that is what
            it will be stored as. */}
        {isAnchor && !isIncluded && (
          <Typography
            variant="caption"
            sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}
          >
            This is one of the anchors. Added, it joins at distance 0 from
            itself and its measured distance from the others.
          </Typography>
        )}
      </Box>
    )}
  </Box>
);

export default SelectedProductPanel;
